import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import DemoExitModal from './DemoExitModal';

/**
 * DemoRouteGuard Component
 *
 * Monitors navigation for demo users and shows a conversion modal
 * when they try to navigate away from allowed demo routes.
 *
 * Allowed routes for demo users:
 * - /garage (main demo page)
 * - /vehicle-report/:vin (vehicle reports from garage)
 * - /vehicle-comparison (comparison mode)
 * - /demo-login (login page)
 */

// Define routes where demo users are allowed
const DEMO_ALLOWED_ROUTES = [
  '/garage',
  '/vehicle-report',
  '/vehicle-comparison',
  '/compare', // Allow demo users to access vehicle comparison
  '/demo-login',
];

const DemoRouteGuard = ({ children, onShowSignUpModal }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDemoUser, setIsDemoUser] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  // Check if current user is a demo user
  useEffect(() => {
    const checkDemoStatus = async () => {
      try {
        if (auth.currentUser) {
          const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const isDemo = userData.isDemo === true;
            setIsDemoUser(isDemo);

            if (isDemo) {
              console.log('🎭 Demo user detected:', auth.currentUser.email);
            }
          }
        } else {
          setIsDemoUser(false);
        }
      } catch (error) {
        console.error('Error checking demo status:', error);
        setIsDemoUser(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkDemoStatus();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(() => {
      checkDemoStatus();
    });

    return () => unsubscribe();
  }, []);

  // Check if a route is allowed for demo users
  const isAllowedRoute = (pathname) => {
    return DEMO_ALLOWED_ROUTES.some((allowedRoute) =>
      pathname.startsWith(allowedRoute)
    );
  };

  // Monitor route changes
  useEffect(() => {
    if (isChecking) return; // Wait for demo check to complete

    const currentPath = location.pathname;

    // If demo user is navigating to a restricted route
    if (isDemoUser && !isAllowedRoute(currentPath)) {
      console.log(
        '🚫 Demo user attempting to navigate to restricted route:',
        currentPath
      );

      // Store the intended navigation
      setPendingNavigation(currentPath);

      // Show conversion modal
      setShowExitModal(true);

      // Track navigation attempt
      if (window.gtag) {
        window.gtag('event', 'demo_exit_attempt', {
          event_category: 'Demo',
          event_label: currentPath,
          attempted_route: currentPath,
        });
      }
    }
  }, [location, isDemoUser, isChecking]);

  // Handle user choosing to sign up
  const handleSignUp = async () => {
    try {
      // Track conversion action
      if (window.gtag) {
        window.gtag('event', 'demo_to_signup', {
          event_category: 'Demo',
          event_label: 'Conversion Modal',
          from_route: location.pathname,
          to_route: pendingNavigation,
        });
      }

      // Sign out demo user
      await auth.signOut();
      sessionStorage.removeItem('isDemoUser');

      // Close demo exit modal
      setShowExitModal(false);

      // Show signup modal instead of navigating
      if (onShowSignUpModal) {
        onShowSignUpModal();
      }
    } catch (error) {
      console.error('Error during demo sign up transition:', error);
      setShowExitModal(false);
    }
  };

  // Handle user choosing to browse as guest
  const handleBrowseAsGuest = async () => {
    try {
      // Track guest browsing action
      if (window.gtag) {
        window.gtag('event', 'demo_to_guest', {
          event_category: 'Demo',
          event_label: 'Browse as Guest',
          from_route: location.pathname,
          to_route: pendingNavigation,
        });
      }

      // Sign out demo user
      await auth.signOut();
      sessionStorage.removeItem('isDemoUser');

      // Close modal
      setShowExitModal(false);

      // Navigate to intended destination
      if (pendingNavigation) {
        navigate(pendingNavigation);
      }
    } catch (error) {
      console.error('Error during demo logout:', error);
      setShowExitModal(false);
    }
  };

  // Handle user choosing to stay in demo
  const handleStayInDemo = () => {
    // Track stay action
    if (window.gtag) {
      window.gtag('event', 'demo_stay', {
        event_category: 'Demo',
        event_label: 'Stay in Demo',
        attempted_route: pendingNavigation,
      });
    }

    // Close modal and navigate back to garage
    setShowExitModal(false);
    setPendingNavigation(null);
    navigate('/garage');
  };

  // Handle modal close (same as stay in demo)
  const handleModalClose = () => {
    setShowExitModal(false);
    setPendingNavigation(null);
    navigate('/garage');
  };

  return (
    <>
      {children}

      {/* Demo Exit Modal */}
      <DemoExitModal
        show={showExitModal}
        onHide={handleModalClose}
        onSignUp={handleSignUp}
        onBrowseAsGuest={handleBrowseAsGuest}
        onStayInDemo={handleStayInDemo}
        intendedDestination={pendingNavigation}
      />
    </>
  );
};

export default DemoRouteGuard;
